import { useTranslation } from "react-i18next";
import React from "react";
import {
  Home,
  Account,
  People,
  Magnet,
  Diamond,
  Contracts,
  Service,
  Partners,
  Tasks,
  Collapse,
  Expand
} from "@dreamhub/designsystem/icons";
import { Nav } from "@/components/ui/nav";
import LogoWithText from "./components/controls/LogoWithText";
import { cn } from "./utils/utils";
import { useSidebarState } from "@/services/context/SidebarContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import { IconButton } from "@/components/ui/icon-button";
import { useOverdueTasksCount } from "@/entities/tasks/hooks";

interface SidebarProps {
  currentPageName?: string;
  onPageClick: (targetName: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPageName, onPageClick }) => {
  const { t } = useTranslation();
  const { isSidebarOpen: isOpen, setSidebarOpen: setIsOpen } =
    useSidebarState();
  const { enabled: showPartnerships } = useFeatureFlag(
    FeatureFlags.CRM_7805_PARTNERSHIPS
  );
  const { enabled: showPartnersTable } = useFeatureFlag(
    FeatureFlags.CRM_7917_PARTNERS_TABLE
  );
  const { enabled: showMyTasksPage } = useFeatureFlag(
    FeatureFlags.CRM_9035_MY_TASKS_PAGE
  );
  const enablePartnerships = useEnablePartnerships();
  const overdueTasksCount = useOverdueTasksCount(showMyTasksPage);
  return (
    <aside
      data-test="sidebar"
      className={cn(
        // Below the banners, so it ends at the window's bottom rather than past it.
        "group bg-surface-sidebar-navigation text-h1 flex flex-col h-[calc(100vh-var(--app-banners-h,0px))] overflow-hidden sticky top-0 left-0",
        "transition-all duration-300 ease-in-out",
        isOpen ? "w-52 max-w-52 min-w-52" : "w-[56px] max-w-[56px] min-w-[56px]"
      )}
    >
      <div className="flex-1" data-test="sidebar-content">
        <LogoWithText isExpanded={isOpen} label={t("dreamhub")} />
        <Nav
          currentPageName={currentPageName}
          isCollapsed={!isOpen}
          onPageClick={onPageClick}
          links={[
            { title: "Home", icon: Home, variant: "default", target: "/" },
            ...(showMyTasksPage
              ? [
                  {
                    title: "Tasks",
                    icon: Tasks,
                    variant: "default" as const,
                    target: "tasks",
                    count: overdueTasksCount,
                    countTooltip: `${overdueTasksCount} overdue ${overdueTasksCount === 1 ? "task" : "tasks"}`,
                    dividerAfter: true
                  }
                ]
              : []),
            {
              title: "Companies",
              icon: Account,
              variant: "default",
              target: "companies"
            },
            {
              title: "People",
              icon: People,
              variant: "default",
              target: "people"
            },
            {
              title: "Leads",
              icon: Magnet,
              variant: "default",
              target: "leads"
            },
            {
              title: "Deals",
              icon: Diamond,
              variant: "default",
              target: "deals"
            },
            {
              title: "Contracts",
              icon: Contracts,
              variant: "default",
              target: "contracts"
            },
            {
              title: "Services",
              icon: Service,
              variant: "default",
              target: "services"
            },
            ...(showPartnerships && showPartnersTable && enablePartnerships
              ? [
                  {
                    title: "Partnerships",
                    icon: Partners,
                    variant: "default" as const,
                    target: "partners"
                  }
                ]
              : [])
          ]}
        />
      </div>
      <div
        className={cn(
          "fixed bottom-0 flex flex-row pb-4 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out",
          isOpen
            ? "w-52 max-w-52 min-w-52 justify-end items-end pr-4"
            : "w-[56px] max-w-[56px] min-w-[56px] justify-center items-center"
        )}
      >
        <IconButton
          onClick={() => setIsOpen(!isOpen)}
          variant="iconNoBg"
          data-test="toggleSidebar"
        >
          {isOpen ? <Collapse /> : <Expand />}
        </IconButton>
      </div>
    </aside>
  );
};

export default Sidebar;
